import React, { useState, useCallback, useMemo, useEffect } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { useSharedState } from '@trello/shared-state';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import {
  smartCardClient,
  ResolveResponse,
} from '@trello/smart-card/smart-card-client';
import type { SmartCardAppearance } from '@trello/smart-card';
import {
  AkSmartCardWithProvider,
  AkSmartCardProps,
} from './AkSmartCardWithProvider';
import { SmartLinkAnalyticsContextType } from './types';
import { ChunkLoadErrorBoundary } from 'app/src/components/ChunkLoadErrorBoundary';

interface Props {
  url: string;
  appearance?: SmartCardAppearance | null;
  plainLink: () => JSX.Element;
  analyticsContext: SmartLinkAnalyticsContextType;
}

const withErrorBoundary = (Component: React.FunctionComponent<Props>) => (
  props: Props,
) => {
  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-teamplates',
        feature: 'Smart Link',
      }}
      errorHandlerComponent={props.plainLink}
    >
      <ChunkLoadErrorBoundary fallback={props.plainLink()}>
        <Component {...props} />
      </ChunkLoadErrorBoundary>
    </ErrorBoundary>
  );
};

// eslint-disable-next-line @trello/no-module-logic
export const SmartLink = withErrorBoundary(
  ({ url, appearance, plainLink, analyticsContext }: Props) => {
    const { source, attributes, containers } = analyticsContext;
    const [shouldRender, setShouldRender] = useState(false);
    const [resolvedUrl, setResolvedUrl] = useState<
      ResolveResponse | undefined
    >();
    const [authorizedProviders] = useSharedState(
      smartCardClient.authorizedProviders,
    );
    const definitionId = resolvedUrl?.meta?.definitionId;
    const shouldSetAkSmartCardProviderKey = !!(
      definitionId && authorizedProviders.has(definitionId)
    );

    const akSmartCardProps: AkSmartCardProps = {
      appearance: appearance || 'inline',
      inheritDimensions: true,
      showActions: false,
    };

    // render Loom links in card comments as "embed" Smart Cards
    if (
      attributes?.fromSection === 'comment' &&
      source === 'cardDetailScreen' &&
      resolvedUrl?.data?.url?.includes('https://www.loom.com')
    ) {
      akSmartCardProps['appearance'] = 'embed';
    }

    // disable inheritDimensions for "embed" Smart Cards
    if (akSmartCardProps['appearance'] === 'embed') {
      akSmartCardProps['inheritDimensions'] = false;
      if (attributes?.fromSection === 'description') {
        akSmartCardProps['isFrameVisible'] = true;
      }
    }

    // This is hacky way to re-render the Smart Cards after the user has completed the authorization
    // flow for a provider. This can go away when @atlaskit/smart-card supports a singleton store prop
    const [akSmartCardProviderKey, setAkSmartCardProviderKey] = useState(
      Date.now(),
    );

    useEffect(() => {
      if (shouldSetAkSmartCardProviderKey) {
        setAkSmartCardProviderKey(Date.now());
      }
    }, [shouldSetAkSmartCardProviderKey]);

    const accessType = resolvedUrl?.meta?.requestAccess?.accessType;
    const cloudId = resolvedUrl?.meta?.requestAccess?.cloudId;
    const urlType = resolvedUrl?.data?.generator?.name;
    const urlAccess = resolvedUrl?.meta?.access;

    const smartLinkAttAnalytics = useMemo(
      () => ({
        source,
        attributes: {
          component: 'smartLink',
          definitionId,
          accessType,
          cloudId,
          urlType,
          urlAccess,
          ...(attributes && attributes),
          ...(containers && containers),
        },
      }),
      [
        source,
        definitionId,
        accessType,
        cloudId,
        urlType,
        urlAccess,
        attributes,
        containers,
      ],
    );

    const handleLinkResolved = useCallback((resolved: ResolveResponse) => {
      setResolvedUrl(resolved);
      setShouldRender(true);
    }, []);

    const handleLinkUnresolved = useCallback(() => {
      setResolvedUrl(undefined);
      setShouldRender(false);
    }, []);

    const handleSmartCardClick = useCallback(() => {
      Analytics.sendClickedLinkEvent({
        linkName: 'smartLink',
        ...smartLinkAttAnalytics,
      });
    }, [smartLinkAttAnalytics]);

    return (
      <AkSmartCardWithProvider
        url={url}
        plainLink={plainLink}
        akSmartCardProps={akSmartCardProps}
        onLinkResolved={handleLinkResolved}
        onLinkUnresolved={handleLinkUnresolved}
        shouldRender={shouldRender}
        onSmartCardClick={handleSmartCardClick}
        analyticsContextData={smartLinkAttAnalytics}
        akSmartCardProviderKey={akSmartCardProviderKey}
      />
    );
  },
);
